import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { NgxPrintService, PrintOptions } from 'ngx-print';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { AssessmentService } from 'src/app/core/service/assessment.service';
import { DocumentService } from 'src/app/core/service/document.service';
import { AssessmentListService } from 'src/app/core/service/grid-data/grid-data-assessment.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Member } from 'src/app/member/model/member';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { Base64Util } from 'src/app/core/utility/base64-util';

@Component({
  standalone: false,
  selector: 'app-document-link-selection',
  templateUrl: './document-link-selection.component.html',
  styleUrls: ['./document-link-selection.component.scss']
})
export class DocumentLinkSelectionComponent
{
  @Input() public memberId: number;
  @Input() public member: Member;
  @Input() public caseId: number;
  @Input() public taskId: string;
  @Input() public consentMemberId: number;
  @Input() public consentLinkMode: 'image-link' | 'consent-request';

  @Output() public closing: EventEmitter<boolean> = new EventEmitter();

  public methodDialogOpened = false;
  public patientLabel = {};
  public loggedInUser = null;
  public loggedInUserRoleText = '';
  public phoneNumberMask: string;
  public selectionForm = undefined;
  public languages: Array<{ text: string; value: string }>;
  public sending = false;
  public assessments: any[] = [];
  public fields: FormlyFieldConfig[] = [];
  public printing = false;

  constructor(
    private printService: NgxPrintService,
    private assessmentService: AssessmentService,
    private settingService: SettingService,
    private userService: UserService,
    private authenticationService: AuthenticationService,
    private fb: UntypedFormBuilder,
    private documentService: DocumentService,
    private assessmentListService: AssessmentListService,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,)
  {
    this.languages = this.userService.preferredLanguages;
  }

  public get methodRequest(): UntypedFormControl
  {
    return this.selectionForm.get('methodRequest');
  }

  public get otherPhone(): UntypedFormControl
  {
    return this.selectionForm.get('otherPhone');
  }

  public get otherEmail(): UntypedFormControl
  {
    return this.selectionForm.get('otherEmail');
  }

  public get preferredLanguage(): UntypedFormControl
  {
    return this.selectionForm.get('preferredLanguage');
  }

  public get consentAssessmentId(): UntypedFormControl
  {
    return this.selectionForm ? this.selectionForm.get('consentAssessmentId') : null;
  }

  @Input() public set open(value: boolean)
  {
    this.printing = false;
    this.methodDialogOpened = value;
    if (value)
    {
      this.loggedInUser = null;
      this.settingService.getCachedSessionSettings().subscribe({
        next: (sessionSetting) =>
        {
          this.patientLabel = { patientLabel: sessionSetting.patientLabel };
          this.phoneNumberMask = sessionSetting.phoneMask;
          this.userService.getUserById(this.authenticationService.currentUser.id, false).subscribe({
            next: (user) =>
            {
              this.loggedInUser = user;
              this.loggedInUserRoleText = this.userService.roles.find(x => x.value === this.loggedInUser.role)?.text;

              this.selectionForm = this.fb.group({
                preferredLanguage: [{ value: 'English', disabled: false }, { validators: [Validators.required] }],
                methodRequest: [{ value: null, disabled: false }, { validators: [Validators.required] }],
                otherPhone: [{ value: null, disabled: true }],
                otherEmail: [{ value: null, disabled: true }],
                consentAssessmentId: [{ value: null, disabled: false }, { validators: (this.consentLinkMode === 'consent-request') ? [Validators.required] : [] }],
              });

              // Load consent type assessments
              if (this.consentLinkMode === 'consent-request') 
              {
                const state = {
                  filter: {
                    filters: [], logic: 'and'
                  },
                  group: [],
                  skip: 0,
                  sort: [{ field: 'name', dir: 'asc' }],
                  take: 99
                };

                state.filter.filters.push({ field: 'consentType', operator: 'eq', value: true });
                state.filter.filters.push({ field: 'deleted', operator: 'eq', value: false });
                state.filter.filters.push({ field: 'active', operator: 'eq', value: true });

                this.assessmentListService.queryByState(state, (assessments: any) =>
                {
                  this.assessments = assessments.data.filter(x => x.source === 'FORM');
                  if (this.assessments.length === 0) 
                  {
                    this.messageHandlerService.displayWarning('CONSENT-ASSESSMENT-NOT-FOUND');
                  }
                  // If only one consent assessment form, select it automatically.
                  else if (this.assessments.length === 1)
                  {
                    this.consentAssessmentId.setValue(this.assessments[0].id);
                  }
                });
              }

              this.selectionForm.get('methodRequest').valueChanges.subscribe({
                next: (method: string) =>
                {
                  this.otherPhone.disable();
                  this.otherPhone.setValidators([]);
                  this.otherEmail.disable();
                  this.otherEmail.setValidators([]);

                  switch (method)
                  {
                    case 'otherSMS':
                      this.otherPhone.enable();
                      this.otherPhone.setValidators([Validators.required]);
                      break;
                    case 'otherEmail':
                      this.otherEmail.enable();
                      this.otherEmail.setValidators([Validators.required, Validators.email]);
                      break;
                    case 'patientEmail':
                    case 'patientSMS':
                      if (this.member.preferredLanguage)
                      {
                        this.setPreferredLanguage(this.member.preferredLanguage);
                      }
                      break;
                    case 'loggedInUserEmail':
                    case 'loggedInUserSMS':
                      if (this.loggedInUser.preferredLanguage)
                      {
                        this.setPreferredLanguage(this.loggedInUser.preferredLanguage);
                      }
                      break;
                  }
                  this.otherPhone.updateValueAndValidity();
                  this.otherEmail.updateValueAndValidity();
                }
              });
            }
          });
        }
      });
    }
  }

  public send(): void
  {
    this.sending = true;

    // Set the request defaults
    const request = {
      consentMemberId: this.consentMemberId,
      consentAssessmentId: this.consentLinkMode === 'consent-request' ? this.consentAssessmentId.value : null,
      memberId: this.memberId ? this.memberId : null,
      caseId: this.caseId ? this.caseId : null,
      taskId: this.taskId ? this.taskId : null,
      type: this.methodRequest.value,
      email: null,
      phone: null,
      preferredLanguage: this.preferredLanguage.value
    };

    // Set the email or phone depending on the user selection
    switch (this.methodRequest.value)
    {
      case 'patientSMS':
        request.phone = this.member?.contact.cellPhoneNumber;
        break;
      case 'patientEmail':
        request.email = this.member?.contact.email;
        break;
      case 'loggedInUserSMS':
        request.phone = this.loggedInUser?.phoneNumber;
        break;
      case 'loggedInUserEmail':
        request.email = this.loggedInUser?.email;
        break;
      case 'otherSMS':
        request.phone = this.otherPhone.value;
        break;
      case 'otherEmail':
        request.email = this.otherEmail.value;
        break;
    }

    this.documentService[this.consentLinkMode === 'consent-request' ? 'sendConsentRequest' : 'sendImageCapture'](request)
      .subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess(this.consentLinkMode === 'consent-request' ? 'CONSENT-REQUEST-LINK-SENT' : 'CAPTURE-LINK-SENT');
          this.closing.emit(true);
          this.messengerService.sendMessage(new MessageSubject('refreshMemberConsent'));
          this.messengerService.sendMessage(new MessageSubject('refreshMemberConsentTab'));
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        },
        complete: () =>
        {
          this.selectionForm = undefined;
          this.sending = false;
        }
      });
  }

  public close(): void
  {
    this.selectionForm = undefined;
    this.closing.emit(false);
  }

  /**
   * Process a print version of the selected Consent form
   */
  public printConsent(): void
  {
    this.assessmentService.getAssessment(this.consentAssessmentId.value).subscribe({
      next: (data) =>
      {
        const fields = JSON.parse(Base64Util.decodeUnicode(data.fields));

        // Mark all required to false prior print
        fields.forEach((f: any) =>
        {
          if (f.templateOptions && f.templateOptions.required) { f.templateOptions.required = false; }
        });

        this.fields = fields;
        this.printing = true;

        const customPrintOptions: PrintOptions = new PrintOptions({
          printSectionId: 'print-section',
          printTitle: data.name,
          useExistingCss: true
        });

        // Print
        setTimeout(() => { this.printService.print(customPrintOptions) });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Set the default preferred language.
   *
   * @param preferredLanguage Language value to set.
   */
  private setPreferredLanguage(preferredLanguage: string): void
  {
    const defaultLanguage = this.languages.find(x => x.value === preferredLanguage);
    if (defaultLanguage)
    {
      this.preferredLanguage.setValue(defaultLanguage.value);
      this.preferredLanguage.updateValueAndValidity();
    }
  }
}
